import React, { useMemo, useState } from 'react';
import { Alert, Button, Divider, Flex } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import collect from 'collect.js';
import useOrderBasket from '../../../hooks/context/useOrderBasket';
import Tips from '../../../components/pages/order-flow/order-confirmation/Tips';
import useOngoingOrder from '../../../hooks/context/useOngoingOrder';
import { CreateOrderData, createOrder } from '../../../services/api/OrderService';
import useStore from '../../../hooks/context/useStore';
import { useNavigate } from 'react-router';
import { Server } from '../../../types/order';
import OrderPaymentBreakdown from '../../../components/payment/OrderPaymentBreakdown';
import DeliveryAmountThreshold from '../../../components/pages/order-flow/order-confirmation/DeliveryAmountThreshold';
import useDeliveryAmountThreshold from '../../../hooks/order/useDeliveryAmountThreshold';
import { Routes } from '../../../routing/Router';
import { AxiosError, AxiosResponse } from 'axios';
import {
  RenderableResponse,
  ResponseStatus,
  ValidationErrorResponse,
} from '../../../services/api/client';
import useNotification from '../../../hooks/context/useNotification';
import OrderFlowStepper from '../../../components/pages/order-flow/OrderFlowStepper';
import PromoCodeInput from '../../../components/pages/order-flow/order-confirmation/PromoCodeInput';
import OrderMethodDetails from '../../../components/pages/order-flow/order-confirmation/OrderMethodDetails';
import OrderTimeDetails from '../../../components/pages/order-flow/order-confirmation/OrderTimeDetails';
import PaymentMethodSelector from '../../../components/pages/order-flow/order-confirmation/PaymentMethodSelector';
import { OrderPaymentType } from '../../../types/constants';
import OrderConfirmationModal from '../../../modals/OrderConfirmationModal';
import OrderItemsList from '../../../components/basket/OrderItemsList';

export default function OrderConfirmationPage() {
  const { storeId } = useStore();
  const { orderData, orderDraft, prepareOrderData, resetOrderData } = useOngoingOrder();
  const { lineItems } = useOrderBasket();

  const { notificationApi } = useNotification();
  const { isWithinThreshold } = useDeliveryAmountThreshold();
  const navigate = useNavigate();

  const [creatingOrder, setCreatingOrder] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const isInputValid = useMemo(
    () => isWithinThreshold && lineItems.length !== 0 && orderData.payment_type,
    [isWithinThreshold, lineItems.length, orderData.payment_type],
  );

  const _createOrder = (extra: Partial<CreateOrderData> = {}) => {
    setCreatingOrder(true);
    setValidationErrors([]);

    const onSuccess = (data: Server.OrderData) => {
      resetOrderData();
      navigate(`/${Routes.Orders}/${data.uuid}`, { state: { afterPurchase: true } });
    };
    const onError = (error: AxiosError) => {
      const handlers = {
        [ResponseStatus.RenderableError]: ({ data }: RenderableResponse) =>
          notificationApi.error({
            message: data.title,
            description: data.description,
          }),
        [ResponseStatus.ValidationError]: ({ data }: ValidationErrorResponse) =>
          setValidationErrors(collect(data.errors).flatten().toArray()),
      };

      return (
        handlers[error.status] ??
        ((response: AxiosResponse) => console.error('Failed to create order', { response }))
      )(error.response);
    };

    createOrder(storeId, prepareOrderData({ method: orderData.method, ...extra }))
      .then(({ data }) => onSuccess(data))
      .catch((error) => onError(error))
      .finally(() => setCreatingOrder(false));
  };

  const onConfirmOrder = () => {
    if (orderData.payment_type === OrderPaymentType.InStore) {
      return _createOrder();
    }

    return setShowConfirmationModal(true);
  };

  return (
    <>
      <Flex justify="space-between">
        <Button
          type="text"
          onClick={() => navigate(`/${Routes.NewOrder.Root}/${Routes.NewOrder.OrderInfo}`)}
          icon={<LeftOutlined />}
          className="my-auto"
        >
          Back to order info
        </Button>
      </Flex>
      <OrderFlowStepper />
      <h1 className="mt-sm">Order confirmation</h1>
      <div className="grid grid-cols-4 gap-xl relative">
        <div className="col-span-3">
          {validationErrors.length > 0 && (
            <Alert
              message="There seems to be an issue with the order. Please check following:"
              description={
                <ul className="list-disc">
                  {validationErrors.map((item: string, index: number) => (
                    <li key={`validation-error-${index}`}>{item}</li>
                  ))}
                </ul>
              }
              type="error"
              showIcon
              className="mt-md"
            />
          )}
          <OrderItemsList data={lineItems} editable className="w-full mt-md" />
        </div>
        {orderDraft && (
          <div className="bg-gray-50 sticky top-sm h-fit p-sm rounded-md max-h-[calc(100vh-24px)] overflow-y-auto">
            <OrderMethodDetails />
            <OrderTimeDetails className="mt-sm" />
            <PromoCodeInput className="mt-sm" />
            <Tips className="mt-sm" />
            <OrderPaymentBreakdown data={orderDraft} animated className="mt-sm" />
            <DeliveryAmountThreshold className="mt-sm" />
            <Divider />
            <PaymentMethodSelector />
            <Button
              type="primary"
              onClick={onConfirmOrder}
              loading={creatingOrder}
              disabled={!isInputValid}
              size="large"
              className="w-full mt-lg"
            >
              Confirm order
            </Button>
          </div>
        )}
      </div>
      <OrderConfirmationModal
        open={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={_createOrder}
      />
    </>
  );
}
